import React, { useState, useEffect } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { Line } from "react-chartjs-2";
import moment from "moment";
import "./Compare.css";
import { toast } from "react-toastify";

function Compare() {
  const [ticker1, setTicker1] = useState("");
  const [ticker2, setTicker2] = useState("");
  const [data, setData] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState("1y");
  const [ticker1Data, setTicker1Data] = useState(null);
  const [ticker2Data, setTicker2Data] = useState(null);
  const [cagr1, setCAGR1] = useState(null);
  const [cagr2, setCAGR2] = useState(null);
  const [chartData, setChartData] = useState({});
  const [loading, setLoading] = useState(false);
  const [analysisAmount, setAnalysisAmount] = useState(5000);
  const [analysisYears, setAnalysisYears] = useState(1);

  const token = useSelector((state) => state.auth.token);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (ticker1 === ticker2) {
      toast.error("The two tickers must not be the same.");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(
        "https://api.stockastix.com/ticker/compare-tickers/",
        { ticker1, ticker2 },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setData(response.data);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const filterDataByCustomYears = (historicalData, years) => {
    const endDate = new Date(historicalData[historicalData.length - 1].date);
    const startDate = new Date(endDate);
    startDate.setFullYear(endDate.getFullYear() - years);

    return historicalData.filter((d) => {
      const dDate = new Date(d.date);
      return dDate >= startDate && dDate <= endDate;
    });
  };

  const calculateInvestmentResult = (historicalData, years, amount) => {
    const filtered = filterDataByCustomYears(historicalData, years);
    if (filtered.length < 2) return null;

    const start = filtered[0].close;
    const end = filtered[filtered.length - 1].close;

    return (amount * end) / start;
  };

  const formatCurrency = (value) => {
    if (!value) return "N/A";
    return `$${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  };

  const filterDataByPeriod = (historicalData) => {
    const endDate = new Date(historicalData[historicalData.length - 1].date);
    let startDate;

    switch (selectedPeriod) {
      case "1y":
        startDate = new Date(endDate);
        startDate.setFullYear(endDate.getFullYear() - 1);
        break;
      case "2y":
        startDate = new Date(endDate);
        startDate.setFullYear(endDate.getFullYear() - 2);
        break;
      case "3y":
        startDate = new Date(endDate);
        startDate.setFullYear(endDate.getFullYear() - 3);
        break;
      case "5y":
        startDate = new Date(endDate);
        startDate.setFullYear(endDate.getFullYear() - 5);
        break;
      default:
        startDate = new Date(endDate);
    }

    const filtered = historicalData.filter((data) => {
      const dataDate = new Date(data.date);
      return dataDate >= startDate && dataDate <= endDate;
    });

    return filtered;
  };

  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
  };

  const formatMarketCap = (marketCap) => {
    if (marketCap === null) return "N/A";
    const billions = (marketCap / 1e9).toFixed(0);
    return `$ ${parseInt(billions).toLocaleString()}B`;
  };

  useEffect(() => {
    if (data) {
      const { ticker1_data, ticker2_data } = data;

      setTicker1Data(ticker1_data);
      setTicker2Data(ticker2_data);

      const filteredData1 = filterDataByPeriod(ticker1_data.historical_data);
      const filteredData2 = filterDataByPeriod(ticker2_data.historical_data);

      const growthData1 = filteredData1.map((item) => ({
        date: item.date,
        growth:
          ((item.close - filteredData1[0].close) / filteredData1[0].close) *
          100,
      }));

      const growthData2 = filteredData2.map((item) => ({
        date: item.date,
        growth:
          ((item.close - filteredData2[0].close) / filteredData2[0].close) *
          100,
      }));

      setChartData({
        labels: growthData1.map((item) =>
          moment(item.date).format("DD MMM, YY")
        ),
        datasets: [
          {
            label: ticker1,
            data: growthData1.map((item) => item.growth),
            borderColor: "rgba(75, 192, 192, 1)",
            fill: false,
            borderWidth: 2,
            pointRadius: 0,
          },
          {
            label: ticker2,
            data: growthData2.map((item) => item.growth),
            borderColor: "rgba(255, 99, 132, 1)",
            fill: false,
            borderWidth: 2,
            pointRadius: 0,
          },
        ],
      });

      const calculateCAGR = (filteredData) => {
        if (filteredData.length < 2) return null;

        const startValue = filteredData[0].close;
        const endValue = filteredData[filteredData.length - 1].close;

        const years = {
          "1y": 1,
          "2y": 2,
          "3y": 3,
          "5y": 5,
        }[selectedPeriod];

        if (!years || startValue <= 0) return null;

        return ((endValue / startValue) ** (1 / years) - 1) * 100;
      };

      setCAGR1(calculateCAGR(filteredData1));
      setCAGR2(calculateCAGR(filteredData2));
    }
    // eslint-disable-next-line
  }, [data, selectedPeriod]);

  return (
    <div className="container">
      <form onSubmit={handleSubmit} className="formm">
        <input
          type="text"
          name="ticker1"
          placeholder="Enter Ticker"
          className="ticker-input mx-1"
          value={ticker1}
          onChange={(e) => setTicker1(e.target.value.toUpperCase())}
        />
        <input
          type="text"
          name="ticker2"
          placeholder="Enter Another Ticker"
          className="ticker-input mx-1"
          value={ticker2}
          onChange={(e) => setTicker2(e.target.value.toUpperCase())}
        />
        <button type="submit" className="ticker-button">
          Compare
        </button>
      </form>

      {loading && (
        <div className="loader-overlay">
          <div className="loader"></div>
        </div>
      )}

      {chartData.labels && (
        <div className="line-chart">
          <div className="period-selection">
            {["1y", "2y", "3y", "5y"].map((period) => (
              <div
                key={period}
                onClick={() => handlePeriodChange(period)}
                className={`period-div ${
                  selectedPeriod === period ? "period-active" : ""
                }`}
              >
                {period}
              </div>
            ))}
          </div>
          <Line
            data={chartData}
            options={{
              responsive: true,
              interaction: { mode: "index", intersect: false },
              plugins: {
                legend: { labels: { color: "#fff" } },
              },
              scales: {
                x: {
                  ticks: { color: "#fff", maxTicksLimit: 12 },
                  grid: { display: false },
                },
                y: { ticks: { color: "#fff" }, grid: { color: "#444444" } },
              },
            }}
            height={100}
          />
        </div>
      )}

      {ticker1Data && ticker2Data && (
        <>
          <div className="extra_details">
            <div className="mcard">
              <div className="mcard_head mb-2">MarketCap</div>
              <div className="metr">
                <div>{ticker1} -</div>
                <div>{formatMarketCap(ticker1Data.stock.market_cap)}</div>
              </div>
              <div className="metr">
                <div>{ticker2} -</div>
                <div>{formatMarketCap(ticker2Data.stock.market_cap)}</div>
              </div>
            </div>
            <div className="mcard">
              <div className="mcard_head mb-2">P/E</div>
              <div className="metr">
                <div>{ticker1} -</div>
                <div>{ticker1Data.stock.pe.toFixed(2)}</div>
              </div>
              <div className="metr">
                <div>{ticker2} -</div>
                <div>{ticker2Data.stock.pe.toFixed(2)}</div>
              </div>
            </div>
            <div className="mcard">
              <div className="mcard_head mb-2">ROE</div>
              <div className="metr">
                <div>{ticker1} -</div>
                <div>{ticker1Data.stock.roe.toFixed(3)}%</div>
              </div>
              <div className="metr">
                <div>{ticker2} -</div>
                <div>{ticker2Data.stock.roe.toFixed(3)}%</div>
              </div>
            </div>
            <div className="mcard">
              <div className="mcard_head mb-2">CAGR</div>
              <div className="metr">
                <div>{ticker1} -</div>
                <div>{cagr1?.toFixed(2)}%</div>
              </div>
              <div className="metr">
                <div>{ticker2} -</div>
                <div>{cagr2?.toFixed(2)}%</div>
              </div>
            </div>
          </div>

          <div className="my-4">
            <h2 className="text-center">Comparision Table</h2>
            <table className="comparision_table">
              <thead>
                <tr>
                  <th className="t_heading_cell">Attribute</th>
                  <th className="t_heading_cell">{ticker1}</th>
                  <th className="t_heading_cell">{ticker2}</th>
                  <th className="t_heading_cell">Better Stock</th>
                </tr>
              </thead>
              <tbody>
                {[
                  {
                    label: "Market Cap",
                    key: "market_cap",
                    format: formatMarketCap,
                    better: (a, b) => (a > b ? ticker1 : ticker2),
                  },
                  {
                    label: "Dividend Yield (%)",
                    key: "dividend_yield",
                    format: (val) =>
                      val !== null && val !== undefined ? `${val}` : "N/A",
                    better: (a, b) => (a > b ? ticker1 : ticker2),
                  },
                  {
                    label: "P/E Ratio",
                    key: "pe",
                    format: (val) =>
                      val !== null && val !== undefined ? val : "N/A",
                    better: (a, b) => (a < b ? ticker1 : ticker2),
                  },
                  {
                    label: "Price-to-Book Ratio",
                    key: "pb_ratio",
                    format: (val) =>
                      val !== null && val !== undefined ? val : "N/A",
                    better: (a, b) => (a < b ? ticker1 : ticker2),
                  },
                  {
                    label: "ROE",
                    key: "roe",
                    format: (val) =>
                      val !== null && val !== undefined ? val : "N/A",
                    better: (a, b) => (a < b ? ticker1 : ticker2),
                  },
                  {
                    label: "Debt-to-Equity",
                    key: "debt_to_equity",
                    format: (val) =>
                      val !== null && val !== undefined ? val : "N/A",
                    better: (a, b) => (a < b ? ticker1 : ticker2),
                  },
                ].map(({ label, key, format, better }) => {
                  const value1 = ticker1Data.stock[key];
                  const value2 = ticker2Data.stock[key];

                  // 🔹 Check if either value is null/undefined before comparing
                  const betterStock =
                    value1 !== null &&
                    value1 !== undefined &&
                    value2 !== null &&
                    value2 !== undefined
                      ? better(value1, value2)
                      : "N/A";

                  return (
                    <tr key={key}>
                      <td className="t_heading_cell">{label}</td>
                      <td className="t_heading_cell">{format(value1)}</td>
                      <td className="t_heading_cell">{format(value2)}</td>
                      <td className="t_heading_cell">{betterStock}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="analysis-section">
            <h2 className="text-center m-4">Investment Analysis</h2>
            <div className="analysis-card">
              {/* Amount Slider */}
              <div>
                <div className="labelss">
                  <div>Investment Amount:</div>
                  <div>{formatCurrency(analysisAmount)}</div>
                </div>
                <input
                  type="range"
                  min="5000"
                  max="100000"
                  step="1000"
                  value={analysisAmount}
                  onChange={(e) => setAnalysisAmount(Number(e.target.value))}
                />
              </div>

              {/* Years Slider */}
              <div>
                <div className="labelss">
                  <div>Time Period:</div>
                  <div>{analysisYears} year(s)</div>
                </div>
                <input
                  type="range"
                  min="1"
                  max="5"
                  step="1"
                  value={analysisYears}
                  onChange={(e) => setAnalysisYears(Number(e.target.value))}
                />
              </div>

              {/* Results */}
              <div className="results">
                <div
                  style={{
                    width: "30%",
                    display: "flex",
                    justifyContent: "space-between",
                    margin: "0px auto",
                  }}
                >
                  <div>{ticker1} :</div>
                  <div>
                    <i>
                      {formatCurrency(
                        calculateInvestmentResult(
                          ticker1Data.historical_data,
                          analysisYears,
                          analysisAmount
                        )
                      )}
                    </i>
                  </div>
                </div>
                <div
                  style={{
                    width: "30%",
                    display: "flex",
                    justifyContent: "space-between",
                    margin: "0px auto",
                  }}
                >
                  <div>{ticker2} :</div>
                  <div style={{ fontStyle: "italic" }}>
                    <i>
                      {formatCurrency(
                        calculateInvestmentResult(
                          ticker2Data.historical_data,
                          analysisYears,
                          analysisAmount
                        )
                      )}
                    </i>
                  </div>
                </div>
              </div>
              <div className="analysis-statement mt-4 text-white font-medium">
                {(() => {
                  const result1 = calculateInvestmentResult(
                    ticker1Data.historical_data,
                    analysisYears,
                    analysisAmount
                  );
                  const result2 = calculateInvestmentResult(
                    ticker2Data.historical_data,
                    analysisYears,
                    analysisAmount
                  );

                  if (result1 && result2) {
                    if (result1 > result2) {
                      return `If you had invested ${formatCurrency(
                        analysisAmount
                      )} for ${analysisYears} year(s), investing in ${ticker1} would have grown it to ${formatCurrency(
                        result1
                      )}, which is higher than ${ticker2} (${formatCurrency(
                        result2
                      )}).`;
                    } else if (result2 > result1) {
                      return `If you had invested ${formatCurrency(
                        analysisAmount
                      )} for ${analysisYears} year(s), investing in ${ticker2} would have grown it to ${formatCurrency(
                        result2
                      )}, which is higher than ${ticker1} (${formatCurrency(
                        result1
                      )}).`;
                    } else {
                      return `If you had invested ${formatCurrency(
                        analysisAmount
                      )} for ${analysisYears} year(s), both ${ticker1} and ${ticker2} would have grown it to the same amount (${formatCurrency(
                        result1
                      )}).`;
                    }
                  }
                  return null;
                })()}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default Compare;
